import { PdfLayout } from '../PdfLayout';
import { FileCode2, Paintbrush, LayoutTemplate, Globe, Lock } from 'lucide-react';

const benefits = [
  {
    icon: FileCode2,
    title: 'HTML, CSS & JavaScript',
    text: 'Write standard web code in the built-in editor with a live preview, and upload the page\'s own images, stylesheets and fonts — no proprietary tools. Or start from a ready-made template.',
  },
  {
    icon: LayoutTemplate,
    title: 'Any Widget in One Line',
    text: 'Place any dashboard widget in your HTML with a <scada-widget> element bound to a tag. Your markup just does the layout.',
  },
  {
    icon: Paintbrush,
    title: 'JavaScript Tag API',
    text: "The scadaink object subscribes to live tags, writes values, queries history and reads data tables — with the viewer's own permissions.",
  },
  {
    icon: Globe,
    title: 'Design with AI',
    text: 'Describe the screen and the AI page designer drafts it from real widgets bound to your tags. You review the code before saving. Requires Scada.Ink Cloud.',
  },
  {
    icon: Lock,
    title: 'Public, Private or Standalone',
    text: 'Make a page public for a wall screen with no sign-in, or keep it behind login. Set it standalone and it always opens full screen, with no app menus.',
  },
];

export function CustomPages() {
  return (
    <PdfLayout
      category="Extensibility"
      headline="Build Any Interface with"
      headlineHighlight="Custom Pages"
      description="When a dashboard grid isn't the right shape, build your own web page: machine mimics, branded wall displays, operator entry screens and custom HMIs — all served directly from the appliance to any browser on your network."
      accentColor="#7c3aed"
      benefits={benefits}
    >
      <CustomPageMockup />
    </PdfLayout>
  );
}

function CustomPageMockup() {
  return (
    <div className="glass-panel p-3 w-full max-w-[280px]">
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-status-fault/60" />
          <div className="w-2 h-2 rounded-full bg-status-idle/60" />
          <div className="w-2 h-2 rounded-full bg-status-running/60" />
        </div>
        <span className="text-[9px] text-text-muted">Custom Page — Operator Panel</span>
      </div>

      {/* Custom HMI mockup */}
      <div className="space-y-1.5">
        {/* Machine header */}
        <div className="glass-card p-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-status-running" />
            <span className="text-[9px] font-semibold text-text-primary">CNC-01 Haas VF-2</span>
          </div>
          <span className="text-[8px] text-status-running font-medium">RUNNING</span>
        </div>

        {/* Live values grid */}
        <div className="grid grid-cols-2 gap-1.5">
          <div className="glass-card p-2 text-center">
            <div className="text-[7px] text-text-muted mb-0.5">Spindle Speed</div>
            <div className="text-base font-bold text-brand-primary tabular-nums">4,218</div>
            <div className="text-[7px] text-text-muted">RPM</div>
          </div>
          <div className="glass-card p-2 text-center">
            <div className="text-[7px] text-text-muted mb-0.5">Feed Rate</div>
            <div className="text-base font-bold text-brand-secondary tabular-nums">120.5</div>
            <div className="text-[7px] text-text-muted">in/min</div>
          </div>
          <div className="glass-card p-2 text-center">
            <div className="text-[7px] text-text-muted mb-0.5">Parts Today</div>
            <div className="text-base font-bold text-status-running tabular-nums">247</div>
            <div className="text-[7px] text-text-muted">of 300 target</div>
          </div>
          <div className="glass-card p-2 text-center">
            <div className="text-[7px] text-text-muted mb-0.5">Coolant Temp</div>
            <div className="text-base font-bold tabular-nums" style={{ color: '#ca8a04' }}>142°</div>
            <div className="text-[7px] text-text-muted">°F</div>
          </div>
        </div>

        {/* Custom buttons */}
        <div className="grid grid-cols-3 gap-1.5">
          <button className="glass-card p-1.5 text-center text-[7px] font-medium text-white bg-status-running rounded-md">
            Start Job
          </button>
          <button className="glass-card p-1.5 text-center text-[7px] font-medium text-white bg-status-idle rounded-md">
            Log Scrap
          </button>
          <button className="glass-card p-1.5 text-center text-[7px] font-medium text-white bg-status-fault rounded-md">
            Call Help
          </button>
        </div>

        {/* Program info */}
        <div className="glass-card p-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[7px] text-text-muted">Current Program</span>
            <span className="text-[7px] font-mono text-brand-primary">O0247.nc</span>
          </div>
          <div className="w-full h-1.5 bg-glass-border rounded-full overflow-hidden">
            <div className="h-full bg-brand-primary rounded-full" style={{ width: '68%' }} />
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-[6px] text-text-muted">Line 1,247 of 1,834</span>
            <span className="text-[6px] text-text-muted">68%</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-2 px-1">
        <span className="text-[8px] text-text-muted">HTML + scadaink API</span>
        <span className="text-[8px] text-status-running font-medium">● Live</span>
      </div>
    </div>
  );
}
